import { useRef, memo } from 'react';

import { DownloadIcon, SaveIcon, SquarePenIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { EditableText } from '@/components/atoms/EditableText/EditableText';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { selectDialogAlerts } from '@/redux/dialogAlerts/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import AlertSummary from '../ErrorSummary/AlertSummary';
import TileMenu from '../Tile/TileMenu';

import styles from './SubHeader.module.scss';

export type SubHeaderProps<T> = {
  onDeleteClick: () => void;
  onExportClick: () => void;
  onImportClick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveClick: () => void;
  onNameChange: (text: string) => void;
  onEditClick: () => void;
  disableSave: boolean;
  disableRename?: boolean;
  disableDelete?: boolean;
  title: string;
  isLoading?: boolean;
  fileExtension?: string;
  variant?: 'default' | 'intents';
  editableTextValidationSchema?: T;
  customSanitizer?: (value: string) => string;
  type?: string;
};

const SubHeader = <T,>({
  onDeleteClick,
  onExportClick,
  onImportClick,
  onSaveClick,
  onNameChange,
  onEditClick,
  disableSave,
  disableRename,
  disableDelete = false,
  title,
  isLoading = false,
  fileExtension,
  variant = 'default',
  editableTextValidationSchema,
  customSanitizer,
  type,
}: SubHeaderProps<T>) => {
  const inputRef = useRef(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { t } = useTranslation();
  const dialogAlerts = useSelector(selectDialogAlerts);
  const dialogErrors = dialogAlerts?.filter((alert) => alert?.alertType === 'error') || [];
  const dialogWarnings = dialogAlerts?.filter((alert) => alert?.alertType === 'warning') || [];
  const isDialog = type === 'dialog';

  const getMenuItems = (disabled = false) => [
    {
      name: t('common.rename'),
      icon: <SquarePenIcon size={16} className="text-muted-foreground" />,
      onClick: onEditClick,
    },
    {
      name: t('common.export'),
      icon: <UploadIcon className="text-muted-foreground" size={16} />,
      onClick: onExportClick,
    },
    {
      name: t('common.import'),
      icon: <DownloadIcon className="text-muted-foreground" size={16} />,
      onClick: () => {
        if (canWrite) {
          inputRef.current.click();
        }
      },
    },
    {
      name: t('common.delete'),
      icon: <Trash2Icon size={16} />,
      onClick: onDeleteClick,
      variant: 'destructive' as const,
      disabled,
    },
  ];

  return (
    <div className={cn({ [styles.header]: true, [styles[`header--${variant}`]]: true })}>
      <div
        className={cn(
          'flex overflow-hidden px-1 py-1 text-muted-foreground',
          isDialog && 'max-w-80'
        )}
      >
        {!title ? (
          <Skeleton className="h-[15px] w-[100px]" />
        ) : (
          <>
            <div ref={nameRef} className={cn(isDialog && 'min-w-0 flex-1')}>
              <EditableText<typeof editableTextValidationSchema>
                defaultValue={title}
                variant="!subheading-semi-bold"
                canEdit={canWrite && !disableRename}
                onSubmit={onNameChange}
                validationSchema={editableTextValidationSchema}
                customSanitizer={customSanitizer}
                maxWidth={isDialog ? 320 : undefined}
                minWidth={isDialog ? 180 : undefined}
                containerRef={isDialog ? nameRef : undefined}
              />
            </div>

            {isDialog && (
              <div className="flex shrink-0 items-center">
                {dialogErrors.length > 0 && (
                  <AlertSummary alertType="error" alerts={dialogErrors} />
                )}

                <AlertSummary alertType="warning" alerts={dialogWarnings} />
              </div>
            )}
          </>
        )}
      </div>
      <div className={styles.actions}>
        <TileMenu menuItems={getMenuItems(disableRename || disableDelete)} />
        <Button
          size="icon-sm"
          isLoading={isLoading}
          onClick={onSaveClick}
          aria-label="save"
          disabled={disableSave}
        >
          <SaveIcon size={16} />
        </Button>
      </div>
      <input
        className={styles.hiddenInput}
        accept={fileExtension}
        onChange={onImportClick}
        ref={inputRef}
        type="file"
      />
    </div>
  );
};

const MemoizedSubHeader = memo(SubHeader) as typeof SubHeader;

export default MemoizedSubHeader;
